import { faFilters } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Button,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { KeyboardEvent, useState } from 'react';

import { backgroundColor } from '../../constants/style';
import ActiveStatusFilterMenu from './ActiveStatusFilterMenu';
import ContractFilterInput from './ContractFilterInput';
import { useContractsContext } from './ContractsContext';
import DateRangeFilter from './DateRangeFilter';
import StateFilterMenu from './StateFilterMenu';

// TODO:
// - add keyboard navigability to the menu/accordion
//    - spacebar to focus inputs within the accordion
// - DRY up handleKeyDown()

export default function ContractsFilters() {
  const { modifyFilters, filters } = useContractsContext();
  const count = Object.keys(filters).length;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const [expanded, setExpanded] = useState<string | false>('panel1');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setExpanded('panel1');
  };
  const closeFilterMenu = (): void => {
    setAnchorEl(null);
  };

  // could be DRYed up
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      closeFilterMenu();
    } else if (e.key === ' ') {
      // should focus on the first input in the accordion
    } else if (e.key === 'Tab') {
      e.stopPropagation();
      e.preventDefault();
      if (expanded && expanded !== 'panel8') {
        const panelNum = parseInt(expanded.charAt(expanded.length - 1), 10);
        setExpanded(`panel${panelNum + 1}`);
      } else {
        setExpanded(false);
        closeFilterMenu();
      }
      if (e.shiftKey) {
        if (expanded && expanded !== 'panel1') {
          const panelNum = parseInt(expanded.charAt(expanded.length - 1), 10);
          setExpanded(`panel${panelNum - 1}`);
        } else {
          setExpanded(false);
          closeFilterMenu();
        }
      }
    } else if (e.key === 'ArrowDown') {
      e.stopPropagation();
      e.preventDefault();
      if (expanded && expanded !== 'panel8') {
        const panelNum = parseInt(expanded.charAt(expanded.length - 1), 10);
        setExpanded(`panel${panelNum + 1}`);
      }
    } else if (e.key === 'ArrowUp') {
      e.stopPropagation();
      e.preventDefault();
      if (expanded && expanded !== 'panel1') {
        const panelNum = parseInt(expanded.charAt(expanded.length - 1), 10);
        setExpanded(`panel${panelNum - 1}`);
      }
    }
  };
  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <>
      <Tooltip title="Add Filters">
        <Button
          variant="text"
          color="primary"
          size="small"
          sx={{ minWidth: 'auto' }}
          id="basic-button"
          aria-controls={isOpen ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
          onClick={handleClick}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <FontAwesomeIcon icon={faFilters} />
            <Badge badgeContent={count} color="primary" />
          </Stack>
        </Button>
      </Tooltip>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={closeFilterMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        onKeyDown={handleKeyDown}
        autoFocus
      >
        <Stack
          direction="column"
          sx={{
            backgroundColor,
          }}
        >
          <Accordion expanded={expanded === 'panel1'} onChange={handleAccordionChange('panel1')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1bh-content" id="panel1bh-header">
              <Typography>Account Manager</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContractFilterInput
                closeFilterMenu={closeFilterMenu}
                pivotType="accountManager"
                placeholder="Account Manager"
                modifyFilters={modifyFilters}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel2'} onChange={handleAccordionChange('panel2')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2bh-content" id="panel2bh-header">
              <Typography>IO Start Date</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DateRangeFilter closeFilterMenu={closeFilterMenu} modifyFilters={modifyFilters} startOrEnd="start" />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel3'} onChange={handleAccordionChange('panel3')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel3bh-content" id="panel3bh-header">
              <Typography>IO End Date</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <DateRangeFilter closeFilterMenu={closeFilterMenu} modifyFilters={modifyFilters} startOrEnd="end" />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel4'} onChange={handleAccordionChange('panel4')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel4bh-content" id="panel4bh-header">
              <Typography>Advertiser Name</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContractFilterInput
                closeFilterMenu={closeFilterMenu}
                pivotType="advertiser"
                placeholder="Advertiser Name"
                modifyFilters={modifyFilters}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel5'} onChange={handleAccordionChange('panel5')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel5bh-content" id="panel5bh-header">
              <Typography>Client Name</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContractFilterInput
                closeFilterMenu={closeFilterMenu}
                pivotType="client"
                placeholder="Client Name"
                modifyFilters={modifyFilters}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel6'} onChange={handleAccordionChange('panel6')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel6bh-content" id="panel6bh-header">
              <Typography>State</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <StateFilterMenu modifyFilters={modifyFilters} closeFilterMenu={closeFilterMenu} />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel7'} onChange={handleAccordionChange('panel7')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel7bh-content" id="panel7bh-header">
              <Typography>Trafficker</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ContractFilterInput
                closeFilterMenu={closeFilterMenu}
                pivotType="trafficker"
                placeholder="Trafficker"
                modifyFilters={modifyFilters}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'panel8'} onChange={handleAccordionChange('panel8')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel8bh-content" id="panel8bh-header">
              <Typography>Active Status</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ActiveStatusFilterMenu modifyFilters={modifyFilters} closeFilterMenu={closeFilterMenu} />
            </AccordionDetails>
          </Accordion>
        </Stack>
      </Menu>
    </>
  );
}
